import MenuIcon from '@mui/icons-material/Menu';
import {
    Avatar,
    Badge,
    Box,
    IconButton,
    ListSubheader,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

import { api_urls } from '../../api/api-urls';
import DEFAULTUSER from '../../assets/images/defaultProfile.svg';
import { getBrandDetails } from '../../services/brand-service';
import { getAllNotifications, type ApiResponse } from '../../services/login-service';
import { RootState, persistor } from '../../store';
import { revertAll } from '../../store/actions';
import { setBrandDetails } from '../../store/reducers/brandSlice';
import { setUserNotificationsCount, updateLoginStatus } from '../../store/reducers/userSlice';
import { trackLogout } from '../../utils/google-analytics';
import { formatToUSDateWithTime } from '../../utils/helper';
import { clearStoragePreservingMigrationCookies } from '../../utils/migration-cookies';
import { NavigationConfig } from '../../utils/navigation-constants';
import ButtonComponent from '../button/ButtonComponent';
import { PrimaryIconWrapper } from '../global-styles/GlobalStyles';
import { Loader } from '../loader';
import { CommonModal } from '../modal/modal';
import { colors } from '../theme/theme';
import { BrandLogo } from '../ui-components/BrandLogo';

export const Header: React.FC<{
    onMenuClick?: () => void;
    isVerificationBannerClosed?: boolean;
    onCloseVerificationBanner?: () => void;
}> = ({ onMenuClick, isVerificationBannerClosed: isVerificationBannerClosedProp, onCloseVerificationBanner }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [notificationAnchorEl, setNotificationAnchorEl] = React.useState<null | HTMLElement>(null);
    const profileMenuContainerRef = React.useRef<HTMLDivElement | null>(null);
    const logoutMenuItemRef = React.useRef<HTMLLIElement | null>(null);
    const notificationMenuContainerRef = React.useRef<HTMLDivElement | null>(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const userDetails = useSelector((state: RootState) => state.user.userDetails);
    const [openLogoutConfirmationModal, setOpenLogoutConfirmationModal] = useState(false);
    const [openAllNotificationsModal, setOpenAllNotificationsModal] = useState(false);
    const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
    const userImageLink = useSelector((state: RootState) => state.user.userImageLink);
    const refreshToken = useSelector((state: RootState) => state.user.refreshToken);
    const [allNotifications, setAllNotifications] = useState<{ Message: string; CreatedTime: string }[]>([]);
    const impersonation = useSelector((state: RootState) => state.impersonation);
    const userNotificationsCount = useSelector((state: RootState) => state.user.userNotificationsCount);
    const [isVerificationBannerClosedInternal, setIsVerificationBannerClosedInternal] = useState(false);
    const isVerificationBannerClosed = onCloseVerificationBanner
        ? (isVerificationBannerClosedProp ?? false)
        : isVerificationBannerClosedInternal;

    // Show banner when user has no phone numbers, or has primary phone but it is not verified
    const phoneVerificationBanner = React.useMemo(() => {
        const phones = userDetails?.phoneNumbers;
        const hasNoPhoneNumbers = !phones?.length;
        const primaryPhone = phones?.find(phone => phone?.IsPrimary === true);
        const isPrimaryUnverified = primaryPhone && !primaryPhone.IsVerified;
        const showBanner = hasNoPhoneNumbers || !!isPrimaryUnverified;
        const message = hasNoPhoneNumbers
            ? 'Please add and verify your Phone Number to complete your profile.'
            : 'Please verify your Phone Number';
        const buttonLabel = hasNoPhoneNumbers ? 'Add & Verify' : 'Verify';
        return { showBanner, message, buttonLabel };
    }, [userDetails?.phoneNumbers]);

    const stripTrailingSlashes = (path: string): string => {
        let end = path.length;
        while (end > 0 && path.charCodeAt(end - 1) === 47 /* '/' */) {
            end--;
        }
        return path.slice(0, end);
    };
    const normalizedPathname = stripTrailingSlashes(location.pathname);
    const isOnSettingsPage =
        normalizedPathname === NavigationConfig.Settings || normalizedPathname.endsWith(NavigationConfig.Settings);

    useEffect(() => {
        if (phoneVerificationBanner.showBanner && !onCloseVerificationBanner) {
            setIsVerificationBannerClosedInternal(false);
        }
    }, [phoneVerificationBanner.showBanner, location.pathname, onCloseVerificationBanner]);

    // Get user initials from name
    const getInitials = (name?: string): string => {
        if (!name) return 'U';
        const nameParts = name.trim().split(/\s+/);
        if (nameParts.length >= 2) {
            // First letter of first word and first letter of last word
            return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
        }
        // If only one word, use first two letters
        return name.substring(0, 2).toUpperCase() || 'U';
    };

    // Get full name - use FirstName + LastName if available, otherwise use Name
    const getFullName = (): string => {
        const firstName = userDetails?.FirstName?.trim() || '';
        const lastName = userDetails?.LastName?.trim() || '';

        if (firstName && lastName) {
            return `${firstName} ${lastName}`;
        }

        if (userDetails?.Name?.trim()) {
            return userDetails.Name.trim();
        }

        return 'User';
    };

    const fullName = getFullName();
    const userInitials = getInitials(fullName !== 'User' ? fullName : undefined);
    const userEmail = userDetails?.Email || '';

    const handleLogout = () => {
        setAnchorEl(null);
        setOpenLogoutConfirmationModal(true);
    };

    const handleLogoutConfirmation = async () => {
        try {
            await axios({
                method: 'POST',
                url: api_urls.Logout,
                headers: {
                    Authorization: refreshToken,
                },
            });
        } catch (error) {
            console.error('Error calling logout API:', error);
        }

        // Track before the store is purged below - userDetails won't exist afterwards
        trackLogout('manual', userDetails?.Id ? String(userDetails.Id) : undefined);

        // Clear Redux store
        dispatch(revertAll());
        dispatch(updateLoginStatus(false));

        // Clear persisted state
        persistor.purge();

        // Clear localStorage (preserve migration cookies so user lands on login page)
        clearStoragePreservingMigrationCookies();

        // Navigate to login (allowed by NoCookieGuard even without migration cookie)
        navigate(`${NavigationConfig.Login}?source=login`);

        // Fetch brand details after logout
        try {
            const response = await getBrandDetails(false);
            if (response.statusCode === 200 && response.data) {
                dispatch(setBrandDetails(response.data));
            }
        } catch (error) {
            console.error('Error fetching brand details after logout:', error);
        }
    };

    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const getPageFocusableElements = (): HTMLElement[] => {
        const focusableSelector = [
            'button:not([disabled])',
            '[href]',
            'input:not([disabled]):not([type="hidden"])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])',
        ].join(', ');

        return Array.from(document.querySelectorAll<HTMLElement>(focusableSelector)).filter(
            element =>
                element.getAttribute('aria-hidden') !== 'true' &&
                element.getAttribute('aria-disabled') !== 'true' &&
                !element.hasAttribute('disabled') &&
                element.offsetParent !== null,
        );
    };

    const focusNextElementAfter = (sourceElement: HTMLElement | null) => {
        const pageFocusables = getPageFocusableElements();
        if (!pageFocusables.length) {
            return;
        }

        const fallbackTarget = document.getElementById('profile-menu-button') as HTMLElement | null;
        const current = sourceElement || fallbackTarget;
        const currentIndex = current ? pageFocusables.indexOf(current) : -1;

        const nextIndex = currentIndex >= 0 && currentIndex < pageFocusables.length - 1 ? currentIndex + 1 : 0;
        pageFocusables[nextIndex]?.focus();
    };

    const handleProfileMenuKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
        if (event.key !== 'Tab') {
            return;
        }

        if (event.shiftKey) {
            return;
        }

        const activeElement = document.activeElement as HTMLElement | null;
        const logoutElement = logoutMenuItemRef.current;

        // First Tab after opening profile menu should focus Logout.
        if (logoutElement && activeElement !== logoutElement) {
            event.preventDefault();
            event.stopPropagation();
            logoutElement.focus();
            return;
        }

        // Next Tab from Logout should move to the next page element.
        if (logoutElement && activeElement === logoutElement) {
            event.preventDefault();
            event.stopPropagation();
            setAnchorEl(null);
            window.requestAnimationFrame(() => {
                focusNextElementAfter(document.getElementById('profile-menu-button') as HTMLElement | null);
            });
        }
    };

    const handleProfileMenuClose = (
        _event: Event | React.SyntheticEvent,
        reason: 'backdropClick' | 'escapeKeyDown' | 'tabKeyDown',
    ) => {
        if (reason === 'tabKeyDown') {
            const logoutElement = logoutMenuItemRef.current;
            const activeElement = document.activeElement as HTMLElement | null;

            // First Tab inside the profile menu should focus Logout instead of closing.
            if (logoutElement && activeElement !== logoutElement) {
                window.requestAnimationFrame(() => {
                    logoutElement.focus();
                });
                return;
            }

            // Next Tab from Logout should close menu and move to next page element.
            handleClose();
            window.requestAnimationFrame(() => {
                focusNextElementAfter(document.getElementById('profile-menu-button') as HTMLElement | null);
            });
            return;
        }

        handleClose();
    };

    const notificationOpen = Boolean(notificationAnchorEl);
    const handleNotificationClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
        setNotificationAnchorEl(event.currentTarget);
        await fetchAllNotifications(true);
    };
    const handleNotificationClose = () => {
        setNotificationAnchorEl(null);
    };

    const getNotificationFocusableElements = (): HTMLElement[] => {
        if (!notificationMenuContainerRef.current) {
            return [];
        }

        const focusableSelector = [
            'button:not([disabled])',
            '[href]',
            'input:not([disabled]):not([type="hidden"])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])',
        ].join(', ');

        return Array.from(notificationMenuContainerRef.current.querySelectorAll<HTMLElement>(focusableSelector)).filter(
            element =>
                element.getAttribute('aria-hidden') !== 'true' &&
                element.getAttribute('aria-disabled') !== 'true' &&
                !element.hasAttribute('disabled'),
        );
    };

    const handleNotificationMenuKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
        if (event.key !== 'Tab') {
            return;
        }

        const focusableElements = getNotificationFocusableElements();
        if (!focusableElements.length) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const currentElement = document.activeElement as HTMLElement | null;
        const currentIndex = currentElement ? focusableElements.indexOf(currentElement) : -1;

        let nextIndex = 0;
        if (event.shiftKey) {
            nextIndex = currentIndex <= 0 ? focusableElements.length - 1 : currentIndex - 1;
        } else {
            nextIndex = currentIndex === -1 || currentIndex === focusableElements.length - 1 ? 0 : currentIndex + 1;
        }

        focusableElements[nextIndex].focus();
    };

    const handleNotificationMenuClose = (
        _event: Event | React.SyntheticEvent,
        reason: 'backdropClick' | 'escapeKeyDown' | 'tabKeyDown',
    ) => {
        if (reason === 'tabKeyDown') {
            return;
        }
        handleNotificationClose();
    };

    // Notification data
    const fetchAllNotifications = async (markAsRead: boolean = false) => {
        setIsLoadingNotifications(true);
        try {
            const response = (await getAllNotifications({ read_all: markAsRead }, false)) as ApiResponse & {
                statusCode?: number;
            };
            if (response.statusCode === 200) {
                setAllNotifications(response.data as { Message: string; CreatedTime: string }[]);
                // Only reset notification count when marking as read (when user clicks notification bell)
                if (markAsRead) {
                    dispatch(setUserNotificationsCount(0));
                }
            }
        } catch (error: unknown) {
            console.error('Error fetching all notifications:', error);
        } finally {
            setIsLoadingNotifications(false);
        }
    };

    const handleViewAllNotifications = () => {
        handleNotificationClose();
        fetchAllNotifications(true);
        setOpenAllNotificationsModal(true);
    };

    const handleCloseAllNotificationsModal = () => {
        setOpenAllNotificationsModal(false);
    };

    const closeLogoutConfirmationModal = () => {
        setOpenLogoutConfirmationModal(false);
        handleClose();
    };

    const handleVerifyPhoneClick = () => {
        navigate(NavigationConfig.Settings, {
            state: {
                enableEditMode: true,
                fromPhoneVerification: true,
            },
        });
    };

    const handleCloseBanner = () => {
        if (onCloseVerificationBanner) {
            onCloseVerificationBanner();
        } else {
            setIsVerificationBannerClosedInternal(true);
        }
    };

    return (
        <header>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                py={1.5}
                px={2}
                sx={{
                    bgcolor: colors.fullWhite,
                    borderBottom: `1px solid ${colors.greyPreset[30]}`,
                }}
            >
                <Stack direction="row" alignItems="center" gap={1}>
                    {isMobile && (
                        <IconButton
                            edge="start"
                            color="primary"
                            aria-label="menu"
                            onClick={onMenuClick}
                            sx={{ p: 0.5 }}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}
                    <Stack onClick={() => navigate(NavigationConfig.Home)} sx={{}}>
                        <BrandLogo
                            alt="aux-cap logo"
                            style={{ height: '100%', width: '100%', maxHeight: 40, maxWidth: 114 }}
                        />
                    </Stack>
                </Stack>
                <Stack direction={'row'} gap={2}>
                    {/* <Box minWidth={280} width={'100%'}>
                    <TextFieldComponent
                        placeholder="Placeholder"
                        type="text"
                        startIcon={
                            <PrimaryIconWrapper>
                                <span className="icon-circle" style={{ fontWeight: 600 }} />
                            </PrimaryIconWrapper>
                        }
                        sx={{
                            '& .MuiInputBase-root': {
                                '& .MuiInputBase-input': {
                                    padding: `${theme.spacing(0.9, 1)} !important`,
                                },
                            },
                            '& .MuiOutlinedInput-root': {
                                pl: 1,
                            },
                        }}
                    />
                </Box> */}
                    <Box sx={{ position: 'relative' }}>
                        <IconButton
                            id="notification-menu-button"
                            sx={{
                                bgcolor: colors.primaryLight[5],
                                height: 'fit-content',
                                pointerEvents: impersonation?.isImpersonating ? 'none' : 'auto',
                            }}
                            onClick={e => {
                                if (impersonation?.isImpersonating) {
                                    e.preventDefault();
                                    return;
                                }
                                handleNotificationClick(e);
                            }}
                            aria-controls="notification-menu"
                            aria-haspopup="menu"
                            aria-expanded={notificationOpen}
                            aria-label={notificationOpen ? 'Close notifications' : 'Open notifications'}
                        >
                            <Stack
                                sx={{
                                    '& span': {
                                        color: theme.palette.text.primary,
                                    },
                                }}
                            >
                                <span className="icon-bell" style={{ fontSize: 18, fontWeight: 600 }} />
                            </Stack>
                            <Badge
                                badgeContent={userNotificationsCount}
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    '& .MuiBadge-badge': {
                                        backgroundColor: colors.errorPreset[10],
                                        color: colors.fullWhite,
                                    },
                                }}
                            />
                        </IconButton>
                    </Box>
                    <Stack
                        id="profile-menu-button"
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        onClick={handleClick}
                        aria-controls="profile-menu"
                        aria-haspopup="menu"
                        aria-expanded={open}
                        aria-label={open ? 'Close profile menu' : 'Open profile menu'}
                        sx={{
                            bgcolor: colors.greyPreset[30],
                            borderRadius: '999px',
                            p: 0.5,
                            height: 'fit-content',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                bgcolor: colors.greyPreset[50],
                            },
                        }}
                        tabIndex={0}
                        role="button"
                    >
                        <Avatar sx={{ width: 30, height: 30 }}>
                            {/* {userInitials} */}
                            {/* {!userImageLink && <span className="icon-user"></span>}
                        {userImageLink && ( */}
                            <img
                                src={userImageLink ? userImageLink : DEFAULTUSER}
                                alt="Profile"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    objectPosition: 'center',
                                    display: 'block',
                                }}
                            />
                            {/* )} */}
                        </Avatar>
                        <PrimaryIconWrapper>
                            {open ? (
                                <span
                                    className="icon-arrow-drop-up"
                                    style={{
                                        fontSize: 16,
                                        fontWeight: 600,
                                        marginRight: 4,
                                        color: colors.fullBlack,
                                    }}
                                />
                            ) : (
                                <span
                                    className="icon-arrow-drop-down"
                                    style={{
                                        fontSize: 16,
                                        fontWeight: 600,
                                        marginRight: 4,
                                        color: colors.fullBlack,
                                    }}
                                />
                            )}
                        </PrimaryIconWrapper>
                    </Stack>
                    <Menu
                        id="profile-menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleProfileMenuClose}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        MenuListProps={{
                            'aria-labelledby': 'profile-menu-button',
                            onKeyDown: handleProfileMenuKeyDown,
                        }}
                        PaperProps={{
                            ref: profileMenuContainerRef,
                            role: 'region',
                            'aria-labelledby': 'profile-menu-button',
                            sx: {
                                borderRadius: '16px',
                                border: `1px solid ${colors.greyPreset[30]}`,
                                mt: 1.5,
                                p: 3,
                                minWidth: 220,
                                backgroundColor: colors.fullWhite,
                                boxShadow: theme.shadows[1],
                                '& .MuiList-root': {
                                    p: 0,
                                },
                            },
                        }}
                    >
                        <Box
                            component="div"
                            role="group"
                            aria-label={userEmail ? `${fullName}, ${userEmail}` : fullName}
                            sx={{ px: 0, py: 0 }}
                        >
                            <Stack alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
                                <Avatar sx={{ bgcolor: colors.warningPreset[10], width: 64, height: 64 }}>
                                    {userImageLink ? (
                                        <img
                                            src={userImageLink}
                                            alt="Profile"
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                objectPosition: 'center',
                                                display: 'block',
                                            }}
                                        />
                                    ) : (
                                        userInitials
                                    )}
                                </Avatar>
                                <Typography variant="body1">{fullName}</Typography>
                                {userEmail && (
                                    <Tooltip title={userEmail}>
                                        <Typography variant="caption" color={colors.greyPreset[80]}>
                                            {userEmail}
                                        </Typography>
                                    </Tooltip>
                                )}
                            </Stack>
                        </Box>
                        <MenuItem
                            ref={logoutMenuItemRef}
                            onClick={handleLogout}
                            sx={{
                                width: '100%',
                                justifyContent: 'center',
                                borderRadius: 2,
                                bgcolor: colors.primaryLight[100],
                                color: colors.fullWhite,
                                minHeight: 32,
                                fontWeight: 700,
                                fontSize: 16,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: colors.primaryLight[70],
                                    color: colors.fullWhite,
                                },
                                '&:focus-visible': {
                                    outline: `2px solid ${colors.fullBlack}`,
                                    outlineOffset: '2px',
                                    color: colors.fullBlack,
                                },
                            }}
                        >
                            <span
                                className="icon-exit"
                                style={{ fontWeight: 700, fontSize: 16, marginRight: 8 }}
                                aria-hidden
                            />
                            <span style={{ fontWeight: 700, fontSize: 16 }}>Logout</span>
                        </MenuItem>
                    </Menu>
                    <Menu
                        id="notification-menu"
                        anchorEl={notificationAnchorEl}
                        open={notificationOpen}
                        onClose={handleNotificationMenuClose}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        MenuListProps={{
                            'aria-labelledby': 'notification-menu-button',
                            onKeyDown: handleNotificationMenuKeyDown,
                        }}
                        PaperProps={{
                            ref: notificationMenuContainerRef,
                            role: 'region',
                            'aria-labelledby': 'notification-menu-button',
                            sx: {
                                borderRadius: '16px',
                                border: `1px solid ${colors.greyPreset[30]}`,
                                mt: 1.5,
                                p: 0,
                                minWidth: 400,
                                maxWidth: 400,
                                backgroundColor: colors.fullWhite,
                                boxShadow: theme.shadows[1],
                                '& .MuiList-root': {
                                    p: 0,
                                },
                            },
                        }}
                    >
                        <ListSubheader
                            component="div"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                lineHeight: 1.5,
                                py: 1.5,
                                px: 2,
                                borderBottom: `1px solid ${colors.greyPreset[30]}`,
                                bgcolor: 'transparent',
                            }}
                        >
                            <Typography variant="h5" component="span" color="textPrimary">
                                Notifications
                            </Typography>
                            <IconButton
                                onClick={handleNotificationClose}
                                sx={{ p: 0.5 }}
                                aria-label="Close notifications menu"
                                role="menuitem"
                            >
                                <span
                                    className="icon-close"
                                    style={{ color: colors.fullBlack, fontSize: 18, fontWeight: 600 }}
                                    aria-hidden
                                />
                            </IconButton>
                        </ListSubheader>
                        <Box component="div" role="group" sx={{ maxHeight: 200, overflowY: 'auto' }}>
                            {isLoadingNotifications ? (
                                <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <Loader />
                                </Box>
                            ) : allNotifications?.length > 0 ? (
                                allNotifications.map((notification, index) => (
                                    <MenuItem
                                        key={index}
                                        disabled
                                        sx={{
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            py: 1,
                                            whiteSpace: 'normal',
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            component="span"
                                            sx={{ display: 'block', mb: 0.5 }}
                                        >
                                            {notification.Message}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary" component="span">
                                            {formatToUSDateWithTime(notification.CreatedTime)}
                                        </Typography>
                                    </MenuItem>
                                ))
                            ) : (
                                <MenuItem disabled sx={{ justifyContent: 'center', py: 2 }}>
                                    <Typography variant="body2" color="textSecondary">
                                        No notifications found
                                    </Typography>
                                </MenuItem>
                            )}
                        </Box>
                        {allNotifications?.length > 0 && (
                            <MenuItem
                                onClick={handleViewAllNotifications}
                                tabIndex={0}
                                sx={{
                                    justifyContent: 'center',
                                    borderTop: `1px solid ${colors.greyPreset[30]}`,
                                    py: 2,
                                    color: colors.primaryLight[100],
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                }}
                            >
                                View All Notifications
                            </MenuItem>
                        )}
                    </Menu>
                </Stack>
                {/* All Notifications Modal */}
                <CommonModal
                    modalOpen={openAllNotificationsModal}
                    primaryBtnClick={handleCloseAllNotificationsModal}
                    secondaryBtnClick={handleCloseAllNotificationsModal}
                    primaryBtnName=""
                    secondaryBtnName="Close"
                    ModalMaxWidth={600}
                    showPrimaryButton={false}
                    DialogMainTitle="All Notifications"
                    DialogSubTitle="View and manage all your notifications in one place"
                    onCloseModal={handleCloseAllNotificationsModal}
                    sx={{
                        '& .MuiDialogContent-root': {
                            p: 0,
                        },
                    }}
                >
                    <Stack>
                        {allNotifications.map((notification, index) => (
                            <Box
                                key={index}
                                sx={{
                                    p: 2,
                                    borderBottom:
                                        index < allNotifications.length - 1
                                            ? `1px solid ${colors.greyPreset[30]}`
                                            : 'none',
                                    '&:hover': {
                                        bgcolor: colors.greyPreset[90],
                                    },
                                }}
                            >
                                {/* <Typography variant="subtitle1" fontWeight={600} mb={0.5}>
                                    {notification.title}
                                </Typography> */}
                                <Typography variant="body2" color="textSecondary" mb={0.5}>
                                    {notification.Message}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {formatToUSDateWithTime(notification.CreatedTime)}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                </CommonModal>
                {/* Logout Confirmation Modal Start */}
                <CommonModal
                    modalOpen={openLogoutConfirmationModal}
                    primaryBtnClick={closeLogoutConfirmationModal}
                    secondaryBtnClick={handleLogoutConfirmation}
                    primaryBtnName="Cancel"
                    secondaryBtnName="Yes"
                    ModalMaxWidth={350}
                    sx={{}}
                    DialogMainTitle="Logout Confirmation"
                    onCloseModal={closeLogoutConfirmationModal}
                >
                    <Typography
                        variant="body1"
                        id="session-timeout-modal-description"
                        color="textSecondary"
                        textAlign="center"
                        sx={{ my: 2 }}
                    >
                        Are you sure, you want to logout?
                    </Typography>
                </CommonModal>
                {/* Logout Confirmation Modal End */}
            </Stack>

            {/* Phone Verification Banner */}
            {phoneVerificationBanner.showBanner &&
                !impersonation?.isImpersonating &&
                !isVerificationBannerClosed &&
                !isOnSettingsPage && (
                    <Box
                        display={'flex'}
                        flexDirection={'column'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        px={3}
                        py={'6px'}
                        sx={{
                            bgcolor: colors.bluePreset[50],
                            color: colors.bluePreset[30],
                        }}
                        width={'100%'}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent={'space-between'}
                            width={'100%'}
                            gap={1.5}
                        >
                            <Stack flex={1} />
                            <Stack direction={'row'} alignItems="center" gap={1.5}>
                                <span className="icon-notification" style={{ fontSize: 16 }} />
                                <Stack mr={0.5}>
                                    <Typography variant="body2">{phoneVerificationBanner.message}</Typography>
                                </Stack>
                                <ButtonComponent
                                    variant="outlined"
                                    onClick={handleVerifyPhoneClick}
                                    sx={{
                                        width: 'fit-content',
                                        minWidth: '40px',
                                        padding: '4px 12.5px !important',
                                        borderRadius: 1,
                                        backgroundColor: 'transparent',
                                    }}
                                    SMButton
                                >
                                    {phoneVerificationBanner.buttonLabel}
                                </ButtonComponent>
                            </Stack>
                            <Stack flex={1} alignItems="flex-end">
                                <IconButton
                                    onClick={handleCloseBanner}
                                    size="small"
                                    aria-label="Close verification banner"
                                    sx={{
                                        color: colors.bluePreset[30],
                                        p: 0,
                                    }}
                                >
                                    <span
                                        className="icon-close"
                                        aria-hidden="true"
                                        style={{
                                            fontSize: 18,
                                            fontWeight: 600,
                                        }}
                                    />
                                </IconButton>
                            </Stack>
                        </Stack>
                    </Box>
                )}
        </header>
    );
};
